import { useEffect, useMemo, useState } from "react";
import {
    getBlockchainLogs,
    searchBlockchainLogs,
} from "../../../api/blockchainExplorerService";

export default function BlockchainExplorerPage() {
    const [logs, setLogs] = useState([]);
    const [query, setQuery] = useState("");
    const [selectedLog, setSelectedLog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const loadLogs = async () => {
        setLoading(true);
        setError("");

        try {
            const data = await getBlockchainLogs();
            setLogs(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Failed to load blockchain logs", err);
            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Failed to load blockchain logs."
            );
            setLogs([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadLogs();
    }, []);

    const handleSearch = async () => {
        setLoading(true);
        setError("");

        try {
            const data = query.trim()
                ? await searchBlockchainLogs(query.trim())
                : await getBlockchainLogs();

            setLogs(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Search failed", err);
            setError(
                err?.response?.data?.message ||
                err?.message ||
                "Blockchain search failed."
            );
        } finally {
            setLoading(false);
        }
    };

    const stats = useMemo(() => {
        const total = logs.length;
        const verified = logs.filter((x) => x.hash || x.currentHash).length;
        const linked = logs.filter((x) => x.previousHash).length;
        const tracked = logs.filter((x) => x.trackingNumber).length;

        return { total, verified, linked, tracked };
    }, [logs]);

    return (
        <div className="w-full">
            <div className="mb-8 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-black text-slate-900">
                        Blockchain Explorer
                    </h1>
                    <p className="text-slate-600 mt-2">
                        View, search and inspect immutable logistics audit events.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={loadLogs}
                    className="rounded-xl bg-slate-800 px-5 py-3 text-white font-semibold hover:bg-slate-900 transition"
                >
                    Refresh Logs
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-6">
                <StatCard title="Total Blocks" value={stats.total} color="blue" />
                <StatCard title="Verified Hashes" value={stats.verified} color="green" />
                <StatCard title="Linked Blocks" value={stats.linked} color="purple" />
                <StatCard title="Tracking Linked" value={stats.tracked} color="yellow" />
            </div>

            <div className="bg-white rounded-2xl shadow border border-slate-200 p-6 mb-6">
                <div className="flex flex-col md:flex-row gap-3">
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") handleSearch();
                        }}
                        placeholder="Search by hash, tracking number, event, user..."
                        className="w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600"
                    />

                    <button
                        type="button"
                        onClick={handleSearch}
                        className="rounded-xl bg-blue-600 px-5 py-3 text-white font-semibold hover:bg-blue-700 transition"
                    >
                        Search
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            setQuery("");
                            setSelectedLog(null);
                            loadLogs();
                        }}
                        className="rounded-xl bg-slate-700 px-5 py-3 text-white font-semibold hover:bg-slate-800 transition"
                    >
                        Reset
                    </button>
                </div>

                {error && (
                    <div className="mt-4 rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                        {error}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <div className="xl:col-span-2 bg-white rounded-2xl shadow border border-slate-200 overflow-hidden">
                    <div className="p-6 border-b">
                        <h2 className="text-2xl font-bold">Audit Chain</h2>
                        <p className="text-slate-500 mt-1">
                            Blockchain audit records stored by the logistics platform.
                        </p>
                    </div>

                    <div className="p-6">
                        {loading ? (
                            <p className="text-slate-500">Loading blockchain logs...</p>
                        ) : logs.length === 0 ? (
                            <p className="text-slate-500">No blockchain records found.</p>
                        ) : (
                            <div className="grid gap-4">
                                {logs.map((log, index) => (
                                    <button
                                        key={log.id || index}
                                        type="button"
                                        onClick={() => setSelectedLog(log)}
                                        className="text-left rounded-2xl border border-slate-200 bg-slate-50 p-5 hover:bg-white hover:shadow-lg transition"
                                    >
                                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-3">
                                            <div className="font-black text-slate-900">
                                                Block #{log.id || index + 1}
                                            </div>

                                            <StatusBadge verified={Boolean(log.hash || log.currentHash)} />
                                        </div>

                                        <div className="grid md:grid-cols-2 gap-3 text-sm">
                                            <Info label="Event" value={log.eventType || log.action || "Blockchain Event"} />
                                            <Info label="Tracking" value={log.trackingNumber || "—"} />
                                            <Info label="Hash" value={formatHash(log.hash || log.currentHash)} />
                                            <Info label="Previous Hash" value={formatHash(log.previousHash)} />
                                            <Info
                                                label="Created"
                                                value={
                                                    log.createdAt || log.timestamp
                                                        ? new Date(log.createdAt || log.timestamp).toLocaleString()
                                                        : "—"
                                                }
                                            />
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className="bg-white rounded-2xl shadow border border-slate-200 overflow-hidden">
                    <div className="p-6 border-b">
                        <h2 className="text-2xl font-bold">Block Details</h2>
                        <p className="text-slate-500 mt-1">
                            Select a block to inspect full audit payload.
                        </p>
                    </div>

                    <div className="p-6">
                        {!selectedLog ? (
                            <p className="text-slate-500">
                                No block selected.
                            </p>
                        ) : (
                            <>
                                <div className="grid gap-3 mb-5">
                                    <Info label="ID" value={selectedLog.id || "—"} />
                                    <Info label="Event" value={selectedLog.eventType || selectedLog.action || "—"} />
                                    <Info label="Tracking" value={selectedLog.trackingNumber || "—"} />
                                    <Info label="Hash" value={formatHash(selectedLog.hash || selectedLog.currentHash)} />
                                </div>

                                <button
                                    type="button"
                                    onClick={() => setSelectedLog(null)}
                                    className="mb-4 rounded-xl bg-slate-800 px-4 py-2 text-white font-semibold hover:bg-slate-900 transition"
                                >
                                    Clear Selection
                                </button>

                                <pre className="max-h-[520px] overflow-auto rounded-xl bg-slate-950 p-4 text-xs text-green-400">
                                    {JSON.stringify(selectedLog, null, 2)}
                                </pre>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function formatHash(hash) {
    if (!hash) return "—";
    return hash.length > 22
        ? `${hash.substring(0, 14)}...${hash.substring(hash.length - 8)}`
        : hash;
}

function StatCard({ title, value, color = "blue" }) {
    const colors = {
        blue: "bg-blue-50 text-blue-700",
        green: "bg-green-50 text-green-700",
        purple: "bg-purple-50 text-purple-700",
        yellow: "bg-yellow-50 text-yellow-700",
    };

    return (
        <div className="bg-white rounded-2xl shadow border border-slate-200 p-6 hover:shadow-xl transition">
            <p className="text-slate-500">{title}</p>
            <div className={`inline-flex mt-3 px-3 py-1 rounded-xl font-black text-3xl ${colors[color]}`}>
                {value}
            </div>
        </div>
    );
}

function Info({ label, value }) {
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                {label}
            </p>
            <p className="font-bold text-slate-900 mt-1 break-words">
                {value}
            </p>
        </div>
    );
}

function StatusBadge({ verified }) {
    return (
        <span
            className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${verified
                    ? "bg-green-100 text-green-700"
                    : "bg-yellow-100 text-yellow-700"
                }`}
        >
            {verified ? "● Verified" : "● Pending"}
        </span>
    );
}